/**
 * Outside links, in the phone app (KPR-1668; docs/plans/ios-app.md, Decision 2).
 *
 * In a browser an `<a target="_blank">` opens a tab by itself, and keepr leaves
 * that alone: a middle-click, a Ctrl-click and "Open link in new tab" keep
 * working because nothing here is installed (`platform.nativeLinks` is false).
 * A WebView has no tabs. There every plain click on such a link goes to
 * `platform.openExternal(url)` (an in-app Safari view; `tel:` and `mailto:` to
 * the system) from the ONE listener below, so the 30-odd links in the app, the
 * links in rendered markdown and the map's attribution all behave the same
 * without each carrying a handler.
 *
 * The links themselves come from components/common/ExternalLink.js. A
 * `target="_blank"` written anywhere else fails src/__tests__/platformSeam.test.js.
 */
import platform from './index';

/** What a link may open from the app. Anything else (javascript:, data:, file:) is refused. */
const OPENABLE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** The absolute address an anchor points at, or null when it points nowhere (no href, `#fragment`). */
export function externalUrl(anchor, origin = '') {
	const href = anchor?.getAttribute?.('href');
	if (!href || href.startsWith('#')) return null;
	try {
		// A relative href is an in-app path opened in a tab: it is the site's address, not the WebView's.
		return new URL(href, origin || undefined).href;
	} catch {
		return null;
	}
}

const isModified = event => Boolean(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (typeof event.button === 'number' && event.button !== 0));

/**
 * The click handler. Answers what it did: 'opened', 'refused' (a link the app
 * will not open, swallowed so the WebView does not navigate to it) or
 * 'ignored' (not its click: the page's own handler, the browser, or not an
 * outside link).
 */
export function handleExternalLinkClick(event, impl = platform) {
	if (!impl?.nativeLinks) return 'ignored';
	if (event.defaultPrevented || isModified(event)) return 'ignored';
	const anchor = event.target?.closest?.('a[target="_blank"]');
	if (!anchor || anchor.hasAttribute('download')) return 'ignored';
	const url = externalUrl(anchor, impl.siteOrigin());
	if (!url) return 'ignored';
	event.preventDefault();
	let protocol = '';
	try { protocol = new URL(url).protocol; } catch { /* refused below */ }
	if (!OPENABLE_PROTOCOLS.has(protocol)) return 'refused';
	impl.openExternal(url);
	return 'opened';
}

const installed = new Map();

/**
 * Listens for clicks on the whole document (bubble phase: a component's own
 * `onClick` has already had its say, and `defaultPrevented` tells us). A
 * no-op on the web. Idempotent. Returns the uninstall.
 */
export function installExternalLinks(impl = platform, doc = typeof document === 'undefined' ? null : document) {
	if (!doc || !impl?.nativeLinks) return () => {};
	if (!installed.has(doc)) {
		const listener = event => { handleExternalLinkClick(event, impl); };
		doc.addEventListener('click', listener);
		installed.set(doc, listener);
	}
	return () => {
		const listener = installed.get(doc);
		if (listener) doc.removeEventListener('click', listener);
		installed.delete(doc);
	};
}
